import React, {createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {useFormikContext} from 'formik';
import {IWithChildren} from 'models/common';

type TFileSelectionLoading = {
  isApplyingFile: boolean;
  startLoading: () => void;
  openFilePicker: () => void;
  applySelectedFile: (file: File) => void;
  clearLoading: () => void;
};

const FileSelectionLoadingContext = createContext<TFileSelectionLoading | null>(null);

export const useFileSelectionLoading = () => useContext(FileSelectionLoadingContext);

const attachFilePath = (file: File, path?: string): File => {
  if (!file || !path) {
    return file;
  }

  try {
    Object.defineProperty(file, 'path', {
      value: path,
      writable: true,
      configurable: true,
    });
  } catch {
    (file as File & {path?: string}).path = path;
  }

  return file;
};

const waitForFileReady = (file: File): Promise<void> => {
  if (file?.type?.startsWith('image/')) {
    const objectUrl = window.URL.createObjectURL(file);

    return new Promise((resolve) => {
      const image = new Image();
      const finish = () => {
        window.URL.revokeObjectURL(objectUrl);
        resolve();
      };

      image.onload = finish;
      image.onerror = finish;
      image.src = objectUrl;
    });
  }

  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
};

interface IFileSelectionLoadingProviderProps extends IWithChildren {
  name: string;
  accept?: string;
}

export const FileSelectionLoadingProvider: FC<IFileSelectionLoadingProviderProps> = ({
  name,
  accept = '*',
  children,
}) => {
  const classes = useStyles();
  const {setFieldValue, setFieldTouched} = useFormikContext<any>();
  const [isApplyingFile, setIsApplyingFile] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dialogOpenRef = useRef(false);
  const applySelectedFileRef = useRef<(file: File) => void>(() => undefined);
  const clearLoadingRef = useRef<() => void>(() => undefined);

  const clearLoading = useCallback(() => {
    dialogOpenRef.current = false;
    setIsApplyingFile(false);
  }, []);

  const startLoading = useCallback(() => {
    setIsApplyingFile(true);
  }, []);

  const finishApply = useCallback(
    async (nextFile: File) => {
      setIsApplyingFile(true);
      setFieldValue(name, nextFile, false);
      setFieldTouched(name, true, false);

      try {
        await waitForFileReady(nextFile);
      } finally {
        // Only hide the spinner after the file is in Formik and ready for the UI.
        dialogOpenRef.current = false;
        setIsApplyingFile(false);
      }
    },
    [name, setFieldTouched, setFieldValue]
  );

  const applySelectedFile = useCallback(
    (nextFile: File) => {
      if (!nextFile) {
        clearLoading();
        return;
      }

      dialogOpenRef.current = false;
      void finishApply(nextFile);
    },
    [clearLoading, finishApply]
  );

  applySelectedFileRef.current = applySelectedFile;
  clearLoadingRef.current = clearLoading;

  const openFilePicker = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }

    dialogOpenRef.current = true;
    // React 16 flushes setState after this handler, so click() runs on the current input node.
    setIsApplyingFile(true);
    input.click();
  }, []);

  // Native listeners stay attached to the same DOM node across React re-renders.
  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) {
      return undefined;
    }

    const handleChange = () => {
      const selectedFile = input.files && input.files[0];
      const selectedPath = input.value;

      if (!selectedFile) {
        // Empty change — treat as cancel if we were waiting on the dialog.
        if (dialogOpenRef.current) {
          clearLoadingRef.current();
        }
        return;
      }

      dialogOpenRef.current = false;
      const fileWithPath = attachFilePath(selectedFile, selectedPath);

      // Reset after reading so the same path can be chosen again.
      input.value = '';

      applySelectedFileRef.current(fileWithPath);
    };

    const handleCancel = () => {
      // Defer so a change event from clicking Open is handled first.
      window.setTimeout(() => {
        if (dialogOpenRef.current) {
          clearLoadingRef.current();
        }
      }, 0);
    };

    input.addEventListener('change', handleChange);
    input.addEventListener('cancel', handleCancel);

    return () => {
      input.removeEventListener('change', handleChange);
      input.removeEventListener('cancel', handleCancel);
    };
  }, []);

  const value = useMemo(() => ({isApplyingFile, startLoading, openFilePicker, applySelectedFile, clearLoading}), [
    isApplyingFile,
    startLoading,
    openFilePicker,
    applySelectedFile,
    clearLoading,
  ]);

  return (
    <FileSelectionLoadingContext.Provider value={value}>
      {children}
      <input ref={fileInputRef} className={classes.hiddenInput} type="file" accept={accept} />
    </FileSelectionLoadingContext.Provider>
  );
};

const useStyles = makeStyles(() => ({
  hiddenInput: {
    display: 'none',
  },
}));
